import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import * as XLSX from 'xlsx';
import './RefundReasonViewer.css';

const RefundReasonViewer = () => {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const refundDataRef = useRef([]);
    const isMounted = useRef(false);
    const [regulationFilter, setRegulationFilter] = useState('');
    const [circumstanceFilter, setCircumstanceFilter] = useState('');
    const [codeFilter, setCodeFilter] = useState('');
    const isDrawing = useRef(false);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false); // State for dropdown
    const navigate = useNavigate();

    const expectedHeaders = useMemo(() => ['Customs Regulation', 'Circumstances', 'ICS Refund Code'], []);

    const applyStyles = () => {
        const isDarkMode = $('body').hasClass('dark-mode');
        $('.custom-filter-input').css({
            height: '35px',
            borderRadius: '6px',
            padding: '8px 12px',
            width: '200px',
            fontSize: '14px',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            background: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#fff' : '#495057',
            boxSizing: 'border-box',
            lineHeight: '1.5',
            marginRight: '10px'
        });
        $('.dt-paging button.dt-paging-button').css({
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#ccc' : '#495057',
            cursor: 'pointer',
            margin: '0 4px',
            boxSizing: 'border-box',
            transition: 'background-color 0.2s, color 0.2s'
        });
        $('.dt-paging button.dt-paging-button:not(.disabled):hover').css({
            backgroundColor: isDarkMode ? '#666' : '#f0f4f8',
            color: isDarkMode ? '#fff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.current').css({
            backgroundColor: isDarkMode ? '#007bff' : '#007bff',
            color: '#fff',
            borderColor: isDarkMode ? '#007bff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.disabled').css({
            cursor: 'not-allowed',
            opacity: '0.5'
        });
        $('#refundTable').addClass('table-visible');
    };

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        const loadRefundData = async () => {
            if (refundDataRef.current.length > 0) return;

            try {
                const response = await fetch('/RefundReason.xlsx');
                if (!response.ok) throw new Error('Failed to fetch Refund Reason data');
                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                if (!firstSheet) throw new Error('Invalid Excel file');

                let loadedRefundData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
                loadedRefundData = loadedRefundData.map(row => {
                    const newRow = {};
                    expectedHeaders.forEach(header => {
                        newRow[header] = row[header] !== undefined ? row[header] : '';
                    });
                    return newRow;
                });

                refundDataRef.current = loadedRefundData;
            } catch (error) {
                alert('Failed to load Refund Reason data. Please try again later.');
                refundDataRef.current = [];
            }

            if (!tableRef.current) return;

            if (dataTableRef.current) {
                dataTableRef.current.destroy(false);
            }

            applyStyles();

            if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                try {
                    dataTableRef.current = $(tableRef.current).DataTable({
                        columns: expectedHeaders.map(header => ({
                            title: header,
                            data: header,
                            render: (data) => data || ''
                        })),
                        columnDefs: [
                            { width: '150px', targets: 0 },
                            { width: 'auto', targets: 1 },
                            { width: '100px', targets: 2 }
                        ],
                        pageLength: 15,
                        lengthMenu: [15, 25, 50, 100],
                        searching: true,
                        ordering: true,
                        order: [[0, 'asc']],
                        responsive: true,
                        language: {
                            search: '',
                            searchPlaceholder: 'Filter records...',
                            lengthMenu: 'Show _MENU_ entries',
                        },
                        dom: '<"table-header"l>t<"table-footer"ip>'
                    });
                    dataTableRef.current.clear().rows.add(refundDataRef.current).draw();
                    dataTableRef.current.on('draw', () => {
                        applyStyles();
                        isDrawing.current = false;
                    });
                    setIsTableInitialized(true);
                } catch (error) {
                    alert('Failed to initialize table.');
                }
            }
        };

        loadRefundData();

        return () => {
            if (dataTableRef.current && !document.getElementById('refundTable')) {
                dataTableRef.current.destroy(false);
            }
            isMounted.current = false;
        };
    }, [expectedHeaders]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    useEffect(() => {
        if (!dataTableRef.current || !isTableInitialized) return;

        const timeoutId = setTimeout(() => {
            if (isDrawing.current) return;
            isDrawing.current = true;

            $.fn.dataTable.ext.search = [];

            const codeFilterFunction = (settings, data, dataIndex, rowData, counter) => {
                if (settings.nTable.id !== 'refundTable') return true;
                const code = String(data[2] || '');
                return codeFilter ? code.includes(codeFilter) : true;
            };

            if (codeFilter) {
                $.fn.dataTable.ext.search.push(codeFilterFunction);
            }

            try {
                dataTableRef.current
                    .column(0)
                    .search(regulationFilter, false, true)
                    .column(1)
                    .search(circumstanceFilter, false, true)
                    .column(2)
                    .search('', false, false)
                    .draw();
            } catch (error) {
                isDrawing.current = false;
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [regulationFilter, circumstanceFilter, codeFilter, isTableInitialized]);

    const handleRegulationFilterChange = useCallback((e) => {
        setRegulationFilter(e.target.value);
    }, []);

    const handleCircumstanceFilterChange = useCallback((e) => {
        setCircumstanceFilter(e.target.value);
    }, []);

    const handleCodeFilterChange = useCallback((e) => {
        setCodeFilter(e.target.value);
    }, []);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = useCallback(() => {
        if (dataTableRef.current) {
            setRegulationFilter('');
            setCircumstanceFilter('');
            setCodeFilter('');
            dataTableRef.current
                .column(0)
                .search('')
                .column(1)
                .search('')
                .column(2)
                .search('')
                .draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
        }
    }, []);

    return (
        <div className="refund-reason-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" />
                    <h1>Refund Reason Codes</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item active" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Viewer</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                                <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                    </nav>
                </div>        
            </header>
            <div className="main-content">
                <div className="table-panel">
                    <div className="table-header">
                        <div id="customFilter">
                            <input
                                type="text"
                                placeholder="Filter Customs Regulation..."
                                value={regulationFilter}
                                onChange={handleRegulationFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter Circumstances..."
                                value={circumstanceFilter}
                                onChange={handleCircumstanceFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter ICS Refund Code..."
                                value={codeFilter}
                                onChange={handleCodeFilterChange}
                                className="custom-filter-input"
                            />
                        </div>
                        <button id="resetTableBtn" onClick={resetTable}>Reset Table</button>
                    </div>
                    <table id="refundTable" className="display" ref={tableRef}></table>
                </div>
            </div>
        </div>
    );
};

export default RefundReasonViewer;